import { useState } from 'react';
import {
  Alert,
  Avatar,
  AvatarStack,
  Button,
  Card,
  Checkbox,
  ConfirmDialog,
  EmptyState,
  Input,
  KpiCard,
  Menu,
  Pill,
  ProgressBar,
  Radio,
  ScoreBar,
  Select,
  SegmentedControl,
  Skeleton,
  Switch,
  Tabs,
  TagInput,
  Textarea,
  Tooltip,
} from '@orion/ui';
import { PageHeader } from '../../app/shell.js';

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <Card className="flex flex-col gap-4 p-5">
      <h2 className="text-xs font-semibold uppercase tracking-wide text-text-muted">{title}</h2>
      {children}
    </Card>
  );
}

/** A living gallery of every shared `@orion/ui` component
 * (design/orion-crm-design/screenshots/Main.png is the design-system
 * board this stands in for) — not gated behind a permission since it's
 * internal reference material, not tenant data. */
export function ComponentLibraryPage() {
  const [tab, setTab] = useState('one');
  const [segment, setSegment] = useState<'me' | 'team'>('me');
  const [checked, setChecked] = useState(true);
  const [tags, setTags] = useState(['Enterprise', 'Renewal']);
  const [confirmOpen, setConfirmOpen] = useState(false);

  return (
    <>
      <PageHeader title="Component library" />
      <main className="flex flex-1 flex-col gap-5 p-7">
        <Section title="Buttons">
          <div className="flex flex-wrap items-center gap-3">
            <Button>Primary</Button>
            <Button variant="secondary">Secondary</Button>
            <Button variant="ghost">Ghost / link</Button>
            <Button variant="danger">Danger</Button>
            <Button disabled>Disabled</Button>
            <Button loading>Saving…</Button>
            <Button size="sm" variant="secondary" aria-label="More actions">
              ⋯
            </Button>
          </div>
        </Section>

        <Section title="Form controls">
          <div className="grid max-w-2xl grid-cols-2 gap-4">
            <Input label="Text input" placeholder="e.g. Jordan Blake" />
            <Input label="With error" error="This field is required." />
            <Select label="Select" options={[{ value: 'a', label: 'Option A' }, { value: 'b', label: 'Option B' }]} placeholder="Choose one" />
            <Textarea label="Textarea" placeholder="Notes…" />
            <Checkbox label="Email me updates" checked={checked} onChange={(e) => setChecked(e.target.checked)} />
            <Radio label="Radio option" name="demo-radio" defaultChecked />
            <Switch label="Enable notifications" checked={checked} onChange={setChecked} />
            <TagInput label="Tags" value={tags} onChange={setTags} placeholder="Add a tag…" />
          </div>
        </Section>

        <Section title="Status pills, tags, temperature, priority">
          <div className="flex flex-wrap gap-2">
            <Pill label="New" color="#2439B3" />
            <Pill label="Contacted" color="#8A4B06" />
            <Pill label="Qualified" color="#1E7A4C" />
            <Pill label="Nurturing" color="#5B34B0" />
            <Pill label="Unqualified" color="#5E6470" />
            <Pill label="Converted" color="#0F6B63" />
            <Pill label="Lost" color="#B42318" />
            <Pill label="Hot" color="#B42318" />
            <Pill label="Warm" color="#8A4B06" />
            <Pill label="Cold" color="#2439B3" />
            <Pill label="▲ High priority" color="#B42318" />
          </div>
        </Section>

        <Section title="Avatars & score">
          <div className="flex items-center gap-6">
            <Avatar name="Alex Morgan" size="lg" />
            <Avatar name="Priya Nair" size="md" />
            <Avatar name="Jordan Blake" size="sm" />
            <AvatarStack names={['Alex Morgan', 'Priya Nair', 'Jordan Blake', 'Sam Ito', 'Chris Lee']} />
            <ScoreBar value={82} />
          </div>
        </Section>

        <Section title="Cards & metrics">
          <div className="grid grid-cols-3 gap-4">
            <KpiCard label="Won revenue" value="$196,000" deltaPct={12} />
            <KpiCard label="Open opportunities" value={18} sub="8 closing this month" />
            <KpiCard label="Conversion" value="32.5%" deltaPct={-4} />
          </div>
          <div className="flex flex-col gap-2">
            <Alert variant="success">Company saved.</Alert>
            <Alert variant="warning">This lead has no email on file.</Alert>
            <Alert variant="danger" errorId="a0b1c2">
              Something went wrong saving this record.
            </Alert>
            <EmptyState title="No opportunities yet" description="Opportunities you create will show up here." />
          </div>
        </Section>

        <Section title="Overlays & navigation">
          <div className="flex flex-col gap-4">
            <Tabs items={[{ key: 'one', label: 'Overview' }, { key: 'two', label: 'Activity' }]} activeKey={tab} onChange={setTab} />
            <SegmentedControl options={[{ value: 'me', label: 'Just me' }, { value: 'team', label: 'My team' }]} value={segment} onChange={setSegment} />
            <div className="flex items-center gap-4">
              <Menu
                trigger={<Button size="sm" variant="secondary">Row actions ▾</Button>}
                items={[{ label: 'Edit', onSelect: () => {} }, { label: 'Archive', onSelect: () => {} }, { label: 'Delete', onSelect: () => {}, variant: 'danger' }]}
              />
              <Tooltip label="This is a tooltip">
                <span className="cursor-default text-sm text-text-secondary underline decoration-dotted">Hover me</span>
              </Tooltip>
              <Button size="sm" onClick={() => setConfirmOpen(true)}>
                Open confirm dialog
              </Button>
            </div>
            <ProgressBar value={60} />
            <div className="flex gap-3">
              <Skeleton className="h-4 w-32" />
              <Skeleton className="h-4 w-20" />
              <Skeleton className="h-4 w-24" />
            </div>
          </div>
        </Section>
      </main>

      <ConfirmDialog
        open={confirmOpen}
        title="Delete this record?"
        description="This can't be undone."
        onConfirm={() => setConfirmOpen(false)}
        onClose={() => setConfirmOpen(false)}
      />
    </>
  );
}
